import * as React from 'react';
import { useNavigate, useParams } from 'react-router';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import ModalDialog from 'components/ModalDialog';
import { showCar, startNewSession, userSession } from 'state/session';

/**
 * Rendered when a dealer opens a scheduled session whose link has been deleted
 * (see `isSessionDeleted` in src/state/session.ts). Replaces the prior "blank
 * screen → silent auto-create new session" behaviour with an explicit dialog.
 *
 * The deleted session's car data is already in `userSession().get('vehicles')`
 * because `loadPublicSession` ran in the route loader. We capture it before
 * starting the new session so the dealer lands on the home dashboard with the
 * same vehicle preloaded.
 */
export default function DeletedSessionRoute() {
  const { brand, market } = useParams<{ brand: string; market: string }>();
  const { _ } = useLingui();
  const session = userSession().useState();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  // INVARIANT (AVS-4080): we intentionally do NOT call clearSession()/resetSession() on
  // mount. The deleted session's `vehicles` map is the source for `preservedVehicle`
  // below — clearing here would break the vehicle handoff into the new session. The
  // stale ended-session state is bounded: every dealer route loader re-checks via
  // `isSessionDeleted` / `sessionEnded` and either routes back here or runs the home
  // redirect with explicit clearSession(), so the state cannot leak into normal flows.
  // Capture vehicle data now so the reset inside startNewSession can't lose it.
  const preservedVehicle = React.useMemo(() => {
    const activeId = session.activeVehicleId;
    if (!activeId) return undefined;
    const vehicle = session.vehicles?.[activeId];
    if (!vehicle?.prString) return undefined;
    return {
      prString: vehicle.prString,
      name: vehicle.carDisplayName || '',
      wltpText: vehicle.wltpText || '',
      okapiCode: vehicle.vinOrMarketingCode || '',
      carDetails: vehicle.carDetails,
      isVtp: vehicle.isVtp,
      vtpDetails: vehicle.vtpDetails,
      vtpPhotos: vehicle.vtpPhotos,
    };
  }, [session.activeVehicleId, session.vehicles]);

  const onStart = React.useCallback(
    (event?: React.MouseEvent<HTMLElement>): boolean | void => {
      // Hand off the click to an async runner. ModalDialog's onClick API is sync —
      // returning `false` keeps the modal open while the new session is being created
      // so the dealer doesn't see a blank flash before the navigate lands.
      event?.preventDefault();
      if (busy) return false;
      setBusy(true);
      setFailed(false);
      void (async () => {
        try {
          // The route loader (`contextLoaderOffline`) sets offline=true so the loaded
          // ended session doesn't sync with the server. Clear it now so the new-session
          // POST inside startNewSession isn't refused by registerSessionOnServer's
          // offline guard.
          userSession().setValues({ offline: false });
          await startNewSession({ replaceActiveSession: true });
          if (preservedVehicle) {
            showCar(preservedVehicle);
          }
          navigate(`/${brand}/${market}/`);
        } catch (error) {
          // Dealer-side UI failure (not an integrator concern): log locally, surface a
          // retry message in the same dialog, and leave the button enabled.
          console.error('DeletedSessionRoute: failed to start a new session', error);
          setFailed(true);
        } finally {
          setBusy(false);
        }
      })();
      return false;
    },
    [brand, busy, market, navigate, preservedVehicle]
  );

  const message = failed
    ? _(msg`Couldn't start a new session. Please try again.`)
    : preservedVehicle
      ? _(msg`This session has been cancelled. You can start a new session with the selected vehicle.`)
      : _(msg`This session has been cancelled. You can start a new session below.`);

  return (
    <ModalDialog
      title={_(msg`Session unavailable`)}
      message={message}
      icon="ex3d-icon--calendar"
      linkText={_(msg`Start a new session`)}
      onClick={onStart}
      disabled={busy}
      data-testid="deleted-session-modal"
    />
  );
}
